import ErrorMessage from '@components/Common/ErrorMessage';
import Loader from '@components/Common/Loader';
import { PageHero } from '@components/Common/Panel';
import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchDripSequences, type DripSequence, type DripSequenceStats } from './api';
import { emailAudienceLabel } from '@helpers/emailAudience';
import './index.scss';

/**
 * The two drip sequences.
 *
 * Modelled as their own object rather than as an email with steps, because a sequence spans several
 * independent templates and its state is per-person, not per-template: `dripCampaignEnrollments`
 * already stores it that way. Each step links out to the normal email record.
 *
 * Cadence and step count come from the backend, which reads the offsets that actually drive
 * sending. Nothing here hardcodes a day number — those live in Java arrays and a copy would go
 * quietly wrong the first time one changed.
 */
const DripSequences: React.FC = () => {
  const navigate = useNavigate();
  const [sequences, setSequences] = useState<DripSequence[]>([]);
  const [stats, setStats] = useState<Record<string, DripSequenceStats>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDripSequences();
      setSequences(data.sequences ?? []);
      setStats(data.stats ?? {});
    } catch {
      setError('Could not load the drip sequences. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Loader />;
  if (error) return <ErrorMessage page message={error} />;

  return (
    <Box className="eo-seq-page">
      <Box className="eo-page-body max-w-container">
        <PageHero
          kicker="Drip sequences"
          title="Multi-step journeys with per-person enrollment state."
          text="Each step is a real email with its own content and performance — open one to see it."
        />

      {sequences.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          No drip sequences are configured.
        </Typography>
      ) : (
        sequences.map((seq) => {
          const s = stats[seq.campaignType];
          const span = seq.steps.length
            ? `Day ${seq.steps[0].dayOffset} → day ${seq.steps[seq.steps.length - 1].dayOffset}`
            : '—';
          return (
            <Box
              key={seq.campaignType}
              className="eo-seq__card"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/email-campaigns/drip-sequences/${seq.campaignType}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/email-campaigns/drip-sequences/${seq.campaignType}`);
                }
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <div className="eo-seq__name">{seq.name}</div>
                <div className="eo-seq__meta">
                  {emailAudienceLabel(seq.audience)} · {seq.stepCount} steps
                  · {span}
                </div>
              </Box>
              <Box className="eo-seq__figures">
                <Box className="eo-seq__figure">
                  <strong>{s?.byStatus?.ACTIVE ?? 0}</strong>
                  <span>Active</span>
                </Box>
                <Box className="eo-seq__figure">
                  <strong>{s?.byStatus?.COMPLETED ?? 0}</strong>
                  <span>Completed</span>
                </Box>
              </Box>
            </Box>
          );
        })
      )}
      </Box>
    </Box>
  );
};

export default DripSequences;
